import { Link, useParams } from "react-router-dom";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Block, EntryHeader, EntryTabs, Panel, RunTab, PromptsTab, MyRunsTab, useEntryTab } from "@/components/run/EntryLayout";
import { NotFoundEntry } from "@/pages/TeamPage";
import { useAuth } from "@/context/AuthContext";
import { useCatalogue } from "@/context/CatalogueContext";
import { agentByWorkingName, agentName, isRequired, shapesOf, statusOf, subAgentsOf, workflowsForAgent } from "@/lib/catalogue";

export const FieldsTable = ({ fields, testid = "form-field-row" }) => (
  <div className="overflow-x-auto">
    <Table>
      <TableHeader><TableRow><TableHead>Field</TableHead><TableHead>Type</TableHead><TableHead>Options</TableHead></TableRow></TableHeader>
      <TableBody>
        {fields.map((f) => {
          const req = f.mandatory === true || String(f.mandatory).toLowerCase() === "true";
          return (
            <TableRow key={f.name} data-testid={testid}>
              <TableCell className="text-sm font-medium">{f.name}{req && <span aria-label="mandatory"> *</span>}</TableCell>
              <TableCell className="text-sm">{f.type}</TableCell>
              <TableCell className="max-w-[360px] text-sm text-muted-foreground">{Array.isArray(f.options) ? f.options.join("; ") : f.options}</TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  </div>
);

export const Needs = ({ inputs = [], fields = [], fieldsLabel = "Form fields", testid = "agent-what-it-needs" }) => (
  <section className="card-elevated p-5" data-testid={testid}>
    <h2 className="text-base font-semibold">What it needs</h2>
    {inputs.length === 0 ? <p className="mt-2 text-sm text-muted-foreground">Nothing listed yet.</p> : (
      <ul className="mt-3 space-y-2 text-[15px]">
        {inputs.map((i) => <li key={i.input}><span className="font-medium">{i.input}</span> <span className="text-sm text-muted-foreground">{isRequired(i.required) ? "Required" : "Optional"}</span>{i.note && <span className="block text-sm text-muted-foreground">{i.note}</span>}</li>)}
      </ul>
    )}
    {fields.length > 0 && (
      <div className="mt-5">
        <p className="mb-2 text-sm font-medium">{fieldsLabel} <span className="font-normal text-muted-foreground">(mandatory fields marked with an asterisk)</span></p>
        <FieldsTable fields={fields} />
      </div>
    )}
  </section>
);

export default function AgentPage() {
  const { empNo } = useParams();
  const { user } = useAuth();
  const { agentsByKey, agents, workflows, loading } = useCatalogue();
  const agent = agentsByKey[empNo];
  const hasRun = !!agent && !agent.parent;
  const { tab, setTab, tabs } = useEntryTab(!!agent?.live, hasRun);
  if (loading) return <Skeleton className="h-64 rounded-xl" />;
  if (!agent) return <NotFoundEntry label="agent" testid="agent-not-found" />;
  const name = agentName(agent);
  const parent = agent.parent ? agentByWorkingName(agent.parent, agents) : null;
  const appears = workflowsForAgent(agent, workflows);
  const subs = subAgentsOf(agent, agents);
  const canRun = hasRun && agent.live && (user.role !== "viewer" || agent.viewer_can_run);
  return (
    <div data-testid="agent-page">
      <EntryHeader entity={agent} id={agent.emp_no} title={name} kindLabel={agent.kind || "Agent"} model={agent.model} shapes={shapesOf(agent)} status={statusOf(agent)} canRun={canRun} onRun={() => { setTab("run"); window.scrollTo({ top: 0, behavior: "smooth" }); }} />
      {parent && (
        <p className="mb-6 rounded-xl bg-muted px-4 py-3 text-sm" data-testid="sub-agent-note">
          This is a sub-agent of <Link to={`/agents/${parent.emp_no}`} className="font-medium underline underline-offset-4">{agentName(parent)}</Link>. It has no run surface of its own; run {agentName(parent)} instead.
        </p>
      )}
      <EntryTabs tabs={tabs} tab={tab} setTab={setTab} />
      {hasRun && <Panel id="run" tab={tab}><RunTab kind="agent" entity={agent} id={agent.emp_no} gates={[]} /></Panel>}
      <Panel id="about" tab={tab}>
        <div className="grid gap-4 lg:grid-cols-3">
          <div className="space-y-4 lg:col-span-2">
            <Block title="Use me for" testid="agent-use-me-for">{agent.use_me_for || agent.function}</Block>
            <Block title="What you get" testid="agent-what-you-get">{agent.what_you_get || "Not described yet."}</Block>
            {agent.bio && <Block title="About" testid="agent-bio">{agent.bio}</Block>}
          </div>
          <div className="space-y-4">
            <Block title="Appears in" testid="agent-appears-in">
              {appears.length === 0 ? "No workflows yet." : <ul className="space-y-1">{appears.map((w) => <li key={w.id}><Link to={`/workflows/${w.id}`} className="font-medium text-foreground underline underline-offset-4" data-testid={`appears-in-${w.id}`}>{w.name}</Link></li>)}</ul>}
            </Block>
            <Block title="Works with" testid="agent-works-with">
              {subs.length === 0 ? "No sub-agents." : (
                <ul className="space-y-2">{subs.map((s) => <li key={s.emp_no}><Link to={`/agents/${s.emp_no}`} className="font-medium text-foreground hover:underline" data-testid={`works-with-${s.emp_no}`}>{agentName(s)}</Link><span className="block text-sm">{s.function}</span></li>)}</ul>
              )}
            </Block>
          </div>
        </div>
      </Panel>
      <Panel id="needs" tab={tab}><Needs inputs={agent.inputs_summary || []} fields={agent.form_fields || []} /></Panel>
      <Panel id="prompts" tab={tab}><PromptsTab id={agent.emp_no} /></Panel>
      <Panel id="runs" tab={tab}><MyRunsTab id={agent.emp_no} tab={tab} /></Panel>
    </div>
  );
}
